import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import {
  fetchDocumentChain,
  type DocumentChain,
} from "@/lib/dokumente";
import {
  defaultChainTabKey,
  DocumentChainPanel,
} from "@/pages/dokumente/DocumentChainPanel";
import { DOCUMENT_EDITOR_OVERLAY_Z } from "@/pages/dokumente/documentEditorLayers";
import { PROJECT_OVERVIEW_Z } from "@/projectOverview/projectOverviewLayers";

export function DocumentChainDialog({
  open,
  onClose,
  chain: chainProp,
  docId,
  currentDocId,
  onOpenDocument,
}: {
  open: boolean;
  onClose: () => void;
  /** Bereits geladene Kette (z. B. aus dem Editor). */
  chain?: DocumentChain | null;
  /** Fallback: Kette per Dokument-ID laden. */
  docId?: number | null;
  currentDocId?: number | null;
  onOpenDocument?: (docId: number) => void;
}) {
  const [chain, setChain] = useState<DocumentChain | null>(chainProp ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabKey, setTabKey] = useState("");

  useEffect(() => {
    if (!open) return;
    if (chainProp) {
      setChain(chainProp);
      setTabKey(defaultChainTabKey(chainProp));
      setError(null);
      setLoading(false);
      return;
    }
    const id = Number(docId);
    if (!Number.isFinite(id) || id <= 0) {
      setChain(null);
      setError("Keine Dokumentenkette verfügbar.");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchDocumentChain(id)
      .then((data) => {
        if (cancelled) return;
        setChain(data);
        setTabKey(defaultChainTabKey(data));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setChain(null);
        setError(err instanceof Error ? err.message : "Dokumentenkette konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, chainProp, docId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Dokumentenkette"
      description="Angebot, Auftragsbestätigung, Stundennachweise und weitere Schritte im Überblick."
      size="xl"
      fullScreenMobile
      zIndexClass={PROJECT_OVERVIEW_Z}
      bodyClassName="min-h-[min(70vh,40rem)]"
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-label-secondary">
          <Spinner />
          Dokumentenkette wird geladen …
        </div>
      ) : error ? (
        <p className="py-8 text-center text-sm text-status-red">{error}</p>
      ) : chain ? (
        <DocumentChainPanel
          chain={chain}
          activeKey={tabKey || chain.current_key}
          onTabChange={setTabKey}
          currentDocId={currentDocId ?? docId ?? null}
          onOpenDocument={onOpenDocument}
          zIndexClass={DOCUMENT_EDITOR_OVERLAY_Z}
        />
      ) : (
        <p className="py-8 text-center text-sm text-label-secondary">Keine Kette vorhanden.</p>
      )}
    </Dialog>
  );
}
